import { useEffect } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  CircleStackIcon,
  CpuChipIcon,
  ArchiveBoxIcon,
  ClockIcon,
  ChartBarIcon,
  BoltIcon,
  Cog6ToothIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '@/lib/auth';
import { projectsApi, schemaApi } from '@/lib/api';
import { formatDistanceToNow } from 'date-fns';

function QuickAction({ to, icon: Icon, label, description }: {
  to: string;
  icon: React.ForwardRefExoticComponent<React.SVGProps<SVGSVGElement> & { title?: string; titleId?: string }>;
  label: string;
  description: string;
}) {
  return (
    <Link
      to={to}
      className="group flex items-start p-4 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50/50 transition-colors"
    >
      <div className="flex-shrink-0 p-2 rounded-md bg-primary-50 text-primary-600 group-hover:bg-primary-100">
        <Icon className="h-5 w-5" />
      </div>
      <div className="ml-4">
        <p className="text-sm font-medium text-gray-900 group-hover:text-primary-700">{label}</p>
        <p className="text-xs text-gray-500 mt-0.5">{description}</p>
      </div>
    </Link>
  );
}

export default function ProjectDetail() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  const { data: projectResponse, isLoading: projectLoading } = useQuery(
    ['project', id],
    () => projectsApi.get(id as string),
    { enabled: isAuthenticated && !!id }
  );

  const { data: tablesResponse } = useQuery(
    ['tables', id],
    () => schemaApi.getTables(id as string),
    { enabled: isAuthenticated && !!id }
  );

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate]);

  if (authLoading || !isAuthenticated) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (projectLoading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-gray-200 rounded w-1/3"></div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-24 bg-gray-200 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  const projectData = projectResponse?.data;
  const project = projectData?.project;
  const status = projectData;

  const tableObjects = Array.isArray(tablesResponse?.data?.tables)
    ? tablesResponse.data.tables
    : Array.isArray(tablesResponse?.tables)
    ? tablesResponse.tables
    : [];

  const tables = tableObjects.map((table: { table_name?: string } | string) =>
    typeof table === 'string' ? table : table.table_name
  ).filter(Boolean);

  if (!project) {
    return (
      <div className="text-center py-12">
        <h1 className="text-2xl font-semibold text-gray-900">Project not found</h1>
        <p className="mt-2 text-gray-600">The project you&apos;re looking for doesn&apos;t exist.</p>
        <Link to="/projects" className="mt-4 btn-primary inline-flex">
          Back to Projects
        </Link>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">{project.name}</h1>
            <p className="mt-1 text-sm text-gray-500">
              {project.description || 'No description provided'}
            </p>
          </div>
          <Link
            to={`/projects/${id}/settings`}
            className="btn-secondary text-sm"
          >
            <Cog6ToothIcon className="h-4 w-4 mr-1.5" />
            Settings
          </Link>
        </div>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="card flex items-center">
          <div className={`p-2 rounded-lg ${project.status === 'active' ? 'bg-green-50' : 'bg-yellow-50'}`}>
            {project.status === 'active' ? (
              <CheckCircleIcon className="h-6 w-6 text-green-600" />
            ) : (
              <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600" />
            )}
          </div>
          <div className="ml-4">
            <p className="text-xs font-medium text-gray-500 uppercase">Status</p>
            <p className={`text-sm font-semibold ${project.status === 'active' ? 'text-green-700' : 'text-yellow-700'}`}>
              {project.status.charAt(0).toUpperCase() + project.status.slice(1)}
              {status?.database_connected === false && (
                <span className="text-red-500 ml-1">(Disconnected)</span>
              )}
            </p>
          </div>
        </div>

        <div className="card flex items-center">
          <div className="p-2 rounded-lg bg-blue-50">
            <CircleStackIcon className="h-6 w-6 text-blue-600" />
          </div>
          <div className="ml-4">
            <p className="text-xs font-medium text-gray-500 uppercase">Tables</p>
            <p className="text-sm font-semibold text-gray-900">{tables.length}</p>
          </div>
        </div>

        <div className="card flex items-center">
          <div className="p-2 rounded-lg bg-purple-50">
            <ClockIcon className="h-6 w-6 text-purple-600" />
          </div>
          <div className="ml-4">
            <p className="text-xs font-medium text-gray-500 uppercase">Created</p>
            <p className="text-sm font-semibold text-gray-900">
              {formatDistanceToNow(new Date(project.created_at), { addSuffix: true })}
            </p>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mb-8">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <QuickAction
            to={`/projects/${id}/query-builder`}
            icon={CpuChipIcon}
            label="Query Builder"
            description="Run SQL queries against your database"
          />
          <QuickAction
            to={`/projects/${id}/schema`}
            icon={CircleStackIcon}
            label="Schema Browser"
            description="View tables, columns, and indexes"
          />
          <QuickAction
            to={`/projects/${id}/backups`}
            icon={ArchiveBoxIcon}
            label="Backups"
            description="Manage database backups"
          />
          <QuickAction
            to={`/projects/${id}/pitr`}
            icon={ClockIcon}
            label="Point-in-Time Recovery"
            description="Restore to any point in time"
          />
          <QuickAction
            to={`/projects/${id}/monitoring`}
            icon={ChartBarIcon}
            label="Monitoring"
            description="Health metrics and slow queries"
          />
          <QuickAction
            to={`/projects/${id}/realtime`}
            icon={BoltIcon}
            label="Real-time"
            description="Live database event streaming"
          />
        </div>
      </div>

      {/* Tables */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium text-gray-900">Database Tables</h2>
          {tables.length > 0 && (
            <Link
              to={`/projects/${id}/schema`}
              className="text-sm text-primary-600 hover:text-primary-700 inline-flex items-center"
            >
              View schema
              <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 ml-1" />
            </Link>
          )}
        </div>
        {tables.length === 0 ? (
          <div className="text-center py-8">
            <CircleStackIcon className="mx-auto h-10 w-10 text-gray-300" />
            <p className="mt-2 text-sm text-gray-500">No tables found in this database.</p>
            <Link
              to={`/projects/${id}/query-builder`}
              className="mt-3 inline-flex items-center text-sm text-primary-600 hover:text-primary-700"
            >
              Open Query Builder to create tables
              <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 ml-1" />
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            {tables.map((table: string) => (
              <div
                key={table}
                className="p-3 bg-gray-50 rounded-md border border-gray-200 hover:border-gray-300 transition-colors"
              >
                <span className="text-sm font-medium text-gray-900">{table}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
